/**
 * @license
 * Copyright 2023 Google LLC
 * SPDX-License-Identifier: Apache-2.0
 * 
 * Part of admin page
 * Component which shows all consultants in a data-table
 */

import { css, html, TemplateResult } from "lit";
import { customElement, property } from "lit/decorators";

import "@maicol07/material-web-additions/data-table/data-table";
import "@maicol07/material-web-additions/data-table/data-table-row";
import "@maicol07/material-web-additions/data-table/data-table-column";
import "@maicol07/material-web-additions/data-table/data-table-cell";

import "@material/mwc-list/mwc-list";
import "@material/mwc-list/mwc-check-list-item";
import "@material/mwc-button";
import "@material/mwc-textfield";
import "@material/mwc-linear-progress";
import "@material/mwc-select";

import "@material/web/textfield/outlined-text-field";
import "@material/web/iconbutton/icon-button";

import { HitlElement } from "../hitl-element";
import { Consultant } from "../data-classes/consultant";

/**
 * Component to manage consultants
 */
@customElement("hitl-consultants")
export class HitlConsultants extends HitlElement {
  /**
   * Consultant array
   */
  @property({type: Array})
  clientConsultants: Consultant[] = [];

  /**
   * Selected consultants
   */
  @property({ type: Array })
  selectedConsultants: Consultant[] = [];

  /**
   * Boolean per default its false. When a new consultant should be created this value becomes true and makes the a new row visible, if the value persists as true.
   */
  @property({ type: Boolean })
  addConsultant: boolean = false;

  /**
   * Dialog text
   */
  dialogText = document.createElement('p');

  /**
   * Ok Button for dialog
   */
  footerButtonOk = document.createElement('mwc-button');

  /**
   * Temp selected rows
   */
  @property({type: Array})
  tempSelectedRows =[];
  
  /**
   * Timer id for connected- and callback
   */
  private timerId: any;

  static override styles = css`
    .mdc-card > div {
      margin: 0px 12px 0 12px;
    }

    .mdc-card__actions {
      gap: 12px;
    }

    md-data-table-footer {
      gap: 10px;
      align-items: center;
    }

    md-outlined-text-field {
      padding: 10px 0 10px;
      width: 100%;
    }

    .mwc-select {
      width: 300%;
    }
    md-data-table-cell {
        background-color: #ffffff;
        
    }
    md-data-table-cell[type="numeric"]{
        text-align: right;
    }
    md-data-table-column {
        background-color: #ffffff;
    }
    md-data-table-footer {
        background-color: #ffffff;
    }
    div mdc-data-table__pagination-trailing {
        background-color: #ffffff;
    }
  `;

  protected override async firstUpdated() {
    const accessToken = await this.getAccessToken(true);
    //Initializes consultants
    if(this.database){
        this.clientConsultants = await Consultant.list(this.database, accessToken);
        this.consultants = await Consultant.list(this.database!, accessToken);
    }
  }

  override connectedCallback() {
    super.connectedCallback();
    //Interval in which consultants are updated each 20 sec. = 20000 msec.
    this.timerId = setInterval(async () => {
      if (this.database) {
        const accessToken = await this.getAccessToken(true);
        this.consultants = await Consultant.list(
          this.database,
          accessToken
        );
      }
      this.requestUpdate();
      console.log("consultant called");
    }, 20000);
  }

  override disconnectedCallback() {
    super.disconnectedCallback();
    //Interval for consultants get disconnected after leaving the page with this component
    if (this.timerId) clearInterval(this.timerId);
    console.log("Clear Interval from disconnectedCallback");
  }

  override render() {
    const template: TemplateResult[] = [];
    template.push(html`
      <div class="mdc-card">
        <div class="card-header">
          <div class="card-title">
            <h2 class="mdc-typography--headline6">Berater Administration</h2>
          </div>
        </div>
        <div class="card-body mdc-typography--body2">
          <md-data-table
            aria-label="Berater Administration"
            density=""
            page-sizes-label="Zeilen pro Seite:"
            first-row-of-page=""
            current-page-size=""
            last-row-of-page=""
            paginated="true"
            pagination-total-label=":firstRow-:lastRow of :totalRows"
            @row-selection-changed=${this.rowSelectionChanged}
            @selected-all=${this.rowSelectionChanged}
            @unselected-all=${this.rowSelectionChanged}>
            <md-data-table-column type="checkbox"></md-data-table-column>
            <md-data-table-column type="numeric" sortable="" filterable=""  filter-text-field-label="Suchen">
              Beraternummer
            </md-data-table-column>
            <md-data-table-column></md-data-table-column>
            <md-data-table-column></md-data-table-column>
            <md-data-table-column></md-data-table-column>
            <md-data-table-column></md-data-table-column>
            <md-data-table-column sortable="" filterable=""  filter-text-field-label="Suchen">
              Name
            </md-data-table-column>
            ${this.addConsultant
              ? html`<md-data-table-row data-id="-1">
                  <md-data-table-cell>
                    <mwc-icon-button @click=${() =>
                        (this.addConsultant =
                            false)} icon="cancel">
                    </mwc-icon-button>
                  </md-data-table-cell>
                  <md-data-table-cell>
                  <md-outlined-text-field type="number" id="consultantID" label="Beraternummer" required @keydown="${this.handleEnterKeySaveNewConsultant}">
                  </md-outlined-text-field>
                  </md-data-table-cell>
                  <md-data-table-cell></md-data-table-cell>
                  <md-data-table-cell></md-data-table-cell>
                  <md-data-table-cell></md-data-table-cell>
                  <md-data-table-cell></md-data-table-cell>
                  <md-data-table-cell>
                    <md-outlined-text-field id="name" label="Name" @keydown="${this.handleEnterKeySaveNewConsultant}">
                    </md-outlined-text-field></md-data-table-cell>
                </md-data-table-row>`
              : null}
            ${this.clientConsultants.map((consultant: Consultant) => {
              return html`
                <md-data-table-row data-id=${consultant.consultantID}>
                  <md-data-table-cell type="checkbox"></md-data-table-cell>
                  <md-data-table-cell type="numeric">${consultant.consultantID}</md-data-table-cell>
                  <md-data-table-cell></md-data-table-cell>
                  <md-data-table-cell></md-data-table-cell>
                  <md-data-table-cell></md-data-table-cell>
                  <md-data-table-cell></md-data-table-cell>
                  <md-data-table-cell>
                  <mwc-textfield
                    outlined
                    id='name-${consultant.consultantID}'
                    TextFieldType="text"
                    value= ${consultant.name}
                    @keydown="${this.handleKeyUpdateConsultantName}"
                    ?disabled=${this.selectedConsultants.length === 0 || this.selectedConsultants.length > 1 || !this.selectedConsultants.includes(consultant)}>
                    ${consultant.name}
                    </mwc-textfield>
                  </md-data-table-cell>
                </md-data-table-row>
              `;
            })}
            <md-data-table-footer
              slot="footer"
              style="display: flex; align-items: left; justify-content: left;">
              <mwc-button
                raised
                @click=${() => (this.addConsultant = true)}
                label="Neuen Berater hinzufügen"
                ?disabled=${this.addConsultant}></mwc-button>
              <mwc-button
                raised
                @click=${this.saveConsultant}
                label="Neuen Berater speichern"
                ?disabled=${!this.addConsultant}></mwc-button>
              <mwc-button
                raised
                @click=${this.deleteConsultants}
                label="Ausgewählte Berater löschen"
                ?disabled=${this.addConsultant ||
                this.selectedConsultants.length === 0}></mwc-button>
              <mwc-button
                raised
                @click=${this.updateConsultantName}
                label="Änderungen speichern"
                ?disabled=${this.addConsultant || this.selectedConsultants.length === 0}
                >
                </mwc-button>
                ${this.selectedConsultants.length > 0
                    ? html`<mwc-button
                    raised
                    @click=${this.cancelEdit}
                    label="Bearbeitung abbrechen"
                    icon="cancel"
                    ></mwc-button>`
                :null}
            </md-data-table-footer>
          </md-data-table>
        </div>
      </div>
    `);
    return template;
  }

  /**
   * Special event from the package maicol07/material-web-additions/data-table/data-table.
   * It triggers if the row selection in the data-table changes
   * @param e 
   */
  rowSelectionChanged = async (e: CustomEvent) => {
    console.log("rowSelectionChanged", e);
    this.tempSelectedRows = e.target!.rows!.filter((row: any) => row.selected);
    this.selectedConsultants = e
      .target!.rows!.filter((row: any) => row.selected)
      .map((row: any) =>
        this.clientConsultants.find(
          (consultant) => consultant.consultantID === BigInt(row.dataset.id)
        )
      );
  };

  /**
   * Updates consultant name
   * @param _ 
   */
  updateConsultantName = async (_:Event) => {           
    const accessToken = await this.getAccessToken(true);
    const consultantName = (this.renderRoot.querySelector(`#name-${this.selectedConsultants[0].consultantID}`) as HTMLInputElement)
    .value;
    //If consultant name exists then the name will be updated
    if(consultantName.length > 0){
      const newConsultant = new Consultant(this.selectedConsultants[0].consultantID, consultantName);
      newConsultant.updateConsultant(this.database!, accessToken).then(() => console.log("Name updated"));
        this.consultants.forEach(consultant =>{
            if(this.selectedConsultants[0].consultantID == consultant.consultantID) consultant.name = consultantName;
        });
        this.tempSelectedRows.forEach((row:any) =>{
            row.selected = false;
        });
        this.selectedConsultants = [];
        this.requestUpdate(); 
    } else {
        this.dialogInit();
        this.dialogText.innerText = "Geben Sie bitte einen Beratername ein";
        this.dialog!.appendChild(this.dialogText);
        this.dialog!.appendChild(this.footerButtonOk);
        this.dialog!.open = true;

        this.footerButtonOk.addEventListener('click', ()=> {
            this.dialog!.open = false;
        });
    }
  };

  /**
   * Handeling interaction with consultant name using keyboard keys
   * @param e 
   */
  handleKeyUpdateConsultantName(e: KeyboardEvent){
    if(e.key === "Enter"){                                          //Updating consultant
        this.updateConsultantName(e as Event);
    }else if(e.key === "Escape"){               //CancelEdit with Escape
        this.cancelEdit(e as Event);
    }
  }

  /**
   * Handeling interaction with new consultant using keyboard keys
   * @param e 
   */
  handleEnterKeySaveNewConsultant(e: KeyboardEvent){                
    if(e.key === "Enter"){
        this.saveConsultant(e as Event);
        
    } else if(e.key === "Escape") {                                 
        this.addConsultant = false;
    }
  }

  /**
   * Save new consultant
   * @param _ 
   */
  saveConsultant = async (_: Event) => {
    let consultantID = (
      this.renderRoot.querySelector("#consultantID") as HTMLInputElement
    ).value;
    let name = (this.renderRoot.querySelector("#name") as HTMLInputElement)
      .value;
    this.dialogInit();
    //If consultant id has min. one number and is not equal 0 and name is not empty then it searches that it does not exists a consultant-client connection 
    if(consultantID.length > 0 && name.length > 0 && consultantID !="0"){
        if(!this.clientConsultants.find((consultant: Consultant) => consultant.consultantID == BigInt(consultantID))){
            const consultant = new Consultant(BigInt(consultantID), name);
            const accessToken = await this.getAccessToken(true);
            //Saves the new consultant
            consultant.save(this.database!, accessToken).then( async ()=> {
              this.clientConsultants = await Consultant.list(this.database!, accessToken)
            });
            //Reset boolean, to not show the creation row
            this.addConsultant = false;
            this.requestUpdate();
        //If consultant-client connection exists, then it shows a pop up window to tell the user to use another consultant id
        } else {
            this.dialogText.innerHTML = "Die angegebene Beraternummer existiert schon";
            this.dialog!.appendChild(this.dialogText);
            this.dialog!.appendChild(this.footerButtonOk);
            this.dialog!.open = true;
        }
    //If the condition is not met then a pop up window with one of the following cases is shown
    }else {
        //If consultant id and name are empty
        if(consultantID.length == 0 && name.length == 0){
            this.dialogText.innerHTML="Geben Sie bitte eine Beraternummer und Name ein!";
            
        }else if(name.length > 0){
            this.dialogText.innerHTML="Geben Sie bitte eine Beraternummer ein!";
        } else if(consultantID == "0"){
            this.dialogText.innerHTML="Geben Sie bitte eine andere Beraternummer ein!";
        }else{
            this.dialogText.innerHTML="Geben Sie bitte einen Beraternamen ein!";
        }
        this.dialog!.appendChild(this.dialogText);
        this.dialog!.appendChild(this.footerButtonOk);
        this.dialog!.open = true;
    }
  };

  cancelEdit = (_:Event) => {
    this.tempSelectedRows.forEach((row: any) => {
        let textfield = (this.shadowRoot?.getElementById(`name-${row.dataset.id}`) as HTMLInputElement);
        const consultant = this.consultants.find((consultant: Consultant) => consultant.consultantID == BigInt(row.dataset.id));
        if(textfield.value != consultant?.name){
            textfield.setAttribute('value', consultant!.name);
            return;   
        }
    });

    this.tempSelectedRows.forEach((row:any)=> {
        row.selected = false;
    });
    this.selectedConsultants = [];
    this.requestUpdate();
  }

  dialogInit(){
    this.dialog!.innerHTML ="";
    this.footerButtonOk.setAttribute("slot", "primaryAction");
    this.footerButtonOk.setAttribute("dialogAction", "ok");
    this.footerButtonOk.innerText = "Ok";
    this.footerButtonOk.classList.add("raised");
    this.dialog!.style.borderColor= "#000000";
    this.dialog!.style.borderWidth= "1px";
    this.dialogText.style.color = "#000000";
    this.dialogText.style.padding = "0px 20%";
    this.dialog!.style.fontSize = "1.1em";
  }

  deleteConsultants = async (_: Event) => {
    const accessToken = await this.getAccessToken(true);
    const selectedConsultantIDs = this.selectedConsultants.map(
      (consultant: Consultant) => consultant.consultantID
    );
    const sql = 
        `DELETE FROM Consultant WHERE consultantID in (${selectedConsultantIDs.join(
        ","
    )})`;
    if(this.database){
      this.database.readWrite(accessToken, sql);
      this.tempSelectedRows.forEach((row:any) =>{
        row.selected = false;
      });
      this.clientConsultants = this.clientConsultants.filter(
      (consultant: Consultant) => !this.selectedConsultants.includes(consultant)
    ); 
    this.selectedConsultants=[];
    }
    this.requestUpdate();
  };
}

declare global {
  interface HTMLElementTagNameMap {
    "hitl-consultants": HitlConsultants;
  }
}